import { 
  collection, 
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { getDefaultTemplatesByBusinessType } from '../utils/templateUtils';

/**
 * Busca todos os templates de WhatsApp de um salão
 * @param {string} salonId - ID do salão
 * @returns {Promise<Array>} Lista de templates
 */
export const getTemplates = async (salonId) => {
  try {
    if (!salonId) return [];
    
    const templatesRef = collection(db, `salons/${salonId}/whatsappTemplates`);
    const templatesSnapshot = await getDocs(templatesRef);
    
    return templatesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Erro ao buscar templates:', error);
    return [];
  }
};

/**
 * Adiciona um novo template de WhatsApp
 * @param {string} salonId - ID do salão
 * @param {object} templateData - Dados do template
 * @returns {Promise<boolean>} Sucesso da operação
 */
export const addTemplate = async (salonId, templateData) => {
  try {
    if (!salonId) return false;
    
    // Se este novo template está sendo definido como padrão
    if (templateData.isDefault) {
      // Encontrar outros templates do mesmo tipo
      const templatesRef = collection(db, `salons/${salonId}/whatsappTemplates`);
      const q = query(templatesRef, 
        where("type", "==", templateData.type),
        where("isDefault", "==", true)
      );
      
      const snapshot = await getDocs(q);
      
      // Desmarcar outros templates do mesmo tipo como não-padrão
      const updatePromises = snapshot.docs.map(doc => 
        updateDoc(doc.ref, { isDefault: false })
      );
      
      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
      }
    }
    
    // Adicionar o novo template
    const templatesRef = collection(db, `salons/${salonId}/whatsappTemplates`);
    await addDoc(templatesRef, {
      ...templateData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Erro ao adicionar template:', error);
    return false;
  }
};

/**
 * Atualiza um template de WhatsApp existente
 * @param {string} salonId - ID do salão
 * @param {string} templateId - ID do template
 * @param {object} templateData - Dados atualizados
 * @returns {Promise<boolean>} Sucesso da operação
 */
export const updateTemplate = async (salonId, templateId, templateData) => {
  try {
    if (!salonId || !templateId) return false;
    
    // Se este template está sendo definido como padrão
    if (templateData.isDefault) {
      // Encontrar outros templates do mesmo tipo
      const templatesRef = collection(db, `salons/${salonId}/whatsappTemplates`);
      const q = query(templatesRef, 
        where("type", "==", templateData.type),
        where("isDefault", "==", true)
      );
      
      const snapshot = await getDocs(q);
      
      // Desmarcar outros templates do mesmo tipo como não-padrão
      const updatePromises = snapshot.docs
        .filter(doc => doc.id !== templateId) // Excluir o template atual
        .map(doc => 
          updateDoc(doc.ref, { isDefault: false })
        );
      
      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
      }
    }
    
    // Atualizar o template
    const templateRef = doc(db, `salons/${salonId}/whatsappTemplates/${templateId}`);
    await updateDoc(templateRef, {
      ...templateData,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Erro ao atualizar template:', error);
    return false;
  }
};

/**
 * Exclui um template de WhatsApp
 * @param {string} salonId - ID do salão
 * @param {string} templateId - ID do template
 * @returns {Promise<boolean>} Sucesso da operação
 */
export const deleteTemplate = async (salonId, templateId) => {
  try {
    if (!salonId || !templateId) return false;
    
    // Buscar todos os templates para encontrar qual estamos excluindo
    const templates = await getTemplates(salonId);
    const templateToDelete = templates.find(t => t.id === templateId);
    
    if (!templateToDelete) {
      throw new Error('Template não encontrado');
    }
    
    // Se estamos excluindo um template padrão, definir outro como padrão
    if (templateToDelete.isDefault) {
      const sameTypeTemplates = templates.filter(
        t => t.type === templateToDelete.type && t.id !== templateId
      );
      
      if (sameTypeTemplates.length > 0) {
        const newDefaultTemplate = sameTypeTemplates[0];
        const templateRef = doc(db, `salons/${salonId}/whatsappTemplates/${newDefaultTemplate.id}`);
        
        await updateDoc(templateRef, {
          isDefault: true,
          updatedAt: serverTimestamp()
        });
      }
    }
    
    // Excluir o template
    const templateRef = doc(db, `salons/${salonId}/whatsappTemplates/${templateId}`);
    await deleteDoc(templateRef);
    
    return true;
  } catch (error) {
    console.error('Erro ao excluir template:', error);
    return false;
  }
};

/**
 * Template padrão para confirmação com links dinâmicos
 */
export const defaultConfirmationLinkTemplate = {
  name: "Confirmação com Links",
  type: "confirmacao",
  isDefault: true,
  message: `Olá {cliente}! 

Seu agendamento para {servico} com {profissional} está marcado para *{data}* às *{hora}*.

Para confirmar a sua presença, por favor, clique no link abaixo:
✅ *CONFIRMAR*: {link_confirmar}

Precisa reagendar?
🗓️ *REAGENDAR*: {link_reagendar}

Precisa cancelar?
❌ *CANCELAR*: {link_cancelar}

Agradecemos a preferência!
{salao}`
};

/**
 * Adiciona templates padrão para um novo salão baseado no tipo de negócio
 * @param {string} salonId - ID do salão recém-criado
 * @param {string} businessType - Tipo de negócio (código interno)
 * @param {string} businessName - Nome do estabelecimento
 * @returns {Promise<boolean>} - Sucesso ou falha
 */
export const createDefaultTemplates = async (salonId, businessType = 'beauty', salonName = 'Meu Salão') => {
  try {
    if (!salonId) throw new Error('ID do salão é obrigatório');
    
    // Obter templates padrão para o tipo de negócio
    const defaultTemplates = getDefaultTemplatesByBusinessType(businessType, salonName);
    
    if (!defaultTemplates || defaultTemplates.length === 0) {
      console.warn(`Nenhum template padrão encontrado para o tipo de negócio: ${businessType}`);
      return false;
    }
    
    // Referência à coleção de templates do salão
    const templatesRef = collection(db, `salons/${salonId}/whatsappTemplates`);
    
    // Verificar se já existem templates para não duplicar
    const existingTemplates = await getDocs(templatesRef);
    if (!existingTemplates.empty) {
      console.log(`Salão ${salonId} já possui templates. Operação ignorada.`);
      return true;
    }
    
    // Adicionar template com links dinâmicos
    const templatesWithLinks = [
      {
        ...defaultConfirmationLinkTemplate,
        // Substituir o nome do salão no template
        message: defaultConfirmationLinkTemplate.message.replace('{salao}', salonName)
      }
    ];
    
    // Adicionar os templates com links dinâmicos
    const promises = [...defaultTemplates, ...templatesWithLinks].map(template => {
      const templateRef = collection(db, `salons/${salonId}/whatsappTemplates`);
      return addDoc(templateRef, {
        ...template,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });
    
    await Promise.all(promises);
    console.log(`${defaultTemplates.length + templatesWithLinks.length} templates padrão adicionados para o salão ${salonId}`);
    
    return true;
  } catch (error) {
    console.error('Erro ao criar templates padrão:', error);
    return false;
  }
};

/**
 * Verifica se um estabelecimento já possui templates
 * @param {string} salonId - ID do salão
 * @returns {Promise<boolean>} - Verdadeiro se já possui templates
 */
export const hasExistingTemplates = async (salonId) => {
  try {
    if (!salonId) return false;
    
    const templatesRef = collection(db, `salons/${salonId}/whatsappTemplates`);
    const existingTemplates = await getDocs(templatesRef);
    
    return !existingTemplates.empty;
  } catch (error) {
    console.error('Erro ao verificar templates existentes:', error);
    return false;
  }
}; 